import React, { useEffect, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Minimize2, Play, Square } from "lucide-react";
import { apiBaseUrl, authHeaders } from "../pages/dispatcher/shared";
import LoadingState from "../pages/dispatcher/LoadingState";
import { TILE_LAYER_ATTRIBUTION, useTileLayerUrl } from "../mapTiles";

// Shared planned-vs-actual route map -- opened from two different places
// (Route History's own row click, and an Alerts card for a toll/off-route/
// eta-drift alert), which is why this lives here instead of staying a
// private function inside RouteHistory.jsx. alertContext is the only thing
// that differs by caller: Route History never passes it (there's no
// "alert" to reference, just a row in that page's own table), so the
// context strip below simply doesn't render for that caller. themeClassName/
// themeVars are the same optional escape hatch: this component's colors are
// all var(--card)/var(--ink)/etc, which resolve to the app's light :root
// values by default -- callers rendered on a dark page (the Operational
// Center) pass their own className + CSS-var override object so this
// portal (which escapes the normal DOM cascade via createPortal) still
// picks up dark colors instead of falling back to light ones.

// Real, bucket-allocated economic loss -- see fleet_stats.fuel_impact_allocate.
// A driver buying more/fewer gallons than planned (a capacity difference) is
// never blended into this figure; only gallons that ARE comparable to a plan
// (fuel_comparable_gallons of fuel_actual_gallons) are priced plan-vs-actual.
// Mirrors FuelCompliance.jsx's own fmtImpact/impactColor sentence phrasing,
// just using this modal's own established red/green palette.
function fmtFuelImpact(n) {
  if (n == null) return "No comparable data";
  if (Math.abs(n) < 0.005) return "On plan";
  const abs = `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return n > 0 ? `${abs} more than plan` : `${abs} less than plan`;
}
function fuelImpactColor(n) {
  if (n == null || Math.abs(n) < 0.005) return "var(--muted)";
  return n > 0 ? "#e0745d" : "#4cae6d";
}

function truckIcon(label, statusColor, isSelected) {
  const ringColor = isSelected ? "#1d6fd8" : "#0b1020";
  const ringWidth = isSelected ? 2.5 : 1.2;
  const scale = isSelected ? 1.15 : 1;
  const html = `
    <div style="display:flex;flex-direction:column;align-items:center;pointer-events:none;transform:scale(${scale});transform-origin:center bottom;">
      <div style="
        background:${isSelected ? "#1d6fd8" : "#12151b"};
        color:${isSelected ? "#0b1020" : "#e5e7eb"};
        border:1px solid ${isSelected ? "#1d6fd8" : "rgba(255,255,255,0.3)"};
        font-size:14px; font-weight:800; padding:4px 11px; border-radius:999px;
        white-space:nowrap; margin-bottom:4px; box-shadow:0 2px 8px rgba(0,0,0,.55);
        letter-spacing:.01em;
      ">${label}</div>
      <svg width="56" height="56" viewBox="0 0 26 26" style="filter:drop-shadow(0 2px 5px rgba(0,0,0,.5));">
        <rect x="1" y="9" width="14" height="8" rx="1.5" fill="${statusColor}" stroke="${ringColor}" stroke-width="${ringWidth}"/>
        <path d="M15 11 h6 l3 4 v2 h-9 z" fill="${statusColor}" stroke="${ringColor}" stroke-width="${ringWidth}"/>
        <rect x="16.5" y="12" width="3.5" height="3" fill="#dff1ea" opacity="0.85"/>
        <circle cx="6" cy="18" r="2.3" fill="#0b0d12" stroke="#e5e7eb" stroke-width="0.8"/>
        <circle cx="20" cy="18" r="2.3" fill="#0b0d12" stroke="#e5e7eb" stroke-width="0.8"/>
      </svg>
    </div>`;
  return L.divIcon({ html, className: "", iconSize: [130, 88], iconAnchor: [65, 72] });
}

// One shared "badge circle with a glyph" shape for every point marker below
// (toll/fuel/deviation) -- same icon LANGUAGE, just a different glyph/color
// per meaning, instead of each type inventing its own shape.
function badgeIcon(glyphSvg, { bg, border = "#0b1020", size = 26 } = {}) {
  const html = `
    <div style="filter:drop-shadow(0 3px 6px rgba(0,0,0,.35));">
      <svg width="${size}" height="${size}" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="11" fill="${bg}" stroke="${border}" stroke-width="1"/>
        ${glyphSvg}
      </svg>
    </div>`;
  return L.divIcon({ html, className: "", iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
}

const GATE_GLYPH = `<path d="M8 9h8v6H8z" fill="#fff"/><path d="M8 9l4-3 4 3" stroke="#fff" stroke-width="1.4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`;
const PUMP_GLYPH_FILL = `<path d="M8 7h5v10H8z" fill="#fff"/><path d="M13 10h2.3c.6 0 1 .4 1 1v3.3c0 .7.9 1 1.2.4" stroke="#fff" stroke-width="1.3" fill="none" stroke-linecap="round"/>`;
const PUMP_GLYPH_HOLLOW_COLOR = "#c9962a";

function tollIcon(source) {
  // Chosen route (this load's actual quoted route) vs. alternate route
  // (matched one of the OTHER routes HERE priced for this load, i.e. the
  // truck detoured onto a path it wasn't quoted for) vs. live_reroute (the
  // truck's real GPS path didn't match ANY of the routes ever priced for
  // this load -- reconstructed after the fact from its own breadcrumb
  // trail, not one of the options dispatch ever saw). That last case is a
  // meaningfully bigger surprise than picking a known alternate, so it
  // gets its own color rather than blending into "alternate route."
  const bg = source === "planned" ? "#1d6fd8" : source === "live_reroute" ? "#d9564a" : "#6ea3d8";
  return badgeIcon(GATE_GLYPH, { bg });
}

function fuelIcon(status) {
  if (status === "hit") return badgeIcon(PUMP_GLYPH_FILL, { bg: "#c9962a" });
  // Unplanned stops split by whether the fill's real GPS position matched a
  // station we actually know about (resolved_truck_stop_id set, verified
  // price) vs one we couldn't identify at all (fleet-average estimate) --
  // very different confidence levels, so they get very different colors
  // rather than one generic "unplanned" red.
  if (status === "unplanned_recognized") return badgeIcon(PUMP_GLYPH_FILL, { bg: "#3b82f6" });
  if (status === "unplanned_unrecognized") return badgeIcon(PUMP_GLYPH_FILL, { bg: "#d9564a" });
  // Planned stop the truck simply hasn't reached yet -- neutral grey pump, NOT
  // the red "missed" hollow pump (an upcoming stop isn't a failure).
  if (status === "upcoming") return badgeIcon(PUMP_GLYPH_FILL, { bg: "#9aa3b2" });
  // missed -- hollow pump, matches the "planned but not filled" dot this replaces
  const html = `
    <div style="filter:drop-shadow(0 3px 6px rgba(0,0,0,.35));">
      <svg width="26" height="26" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="11" fill="#fff" stroke="${PUMP_GLYPH_HOLLOW_COLOR}" stroke-width="2"/>
        <path d="M8 7h5v10H8z" fill="none" stroke="${PUMP_GLYPH_HOLLOW_COLOR}" stroke-width="1.3"/>
        <path d="M13 10h2.3c.6 0 1 .4 1 1v3.3c0 .7.9 1 1.2.4" stroke="${PUMP_GLYPH_HOLLOW_COLOR}" stroke-width="1.3" fill="none" stroke-linecap="round"/>
      </svg>
    </div>`;
  return L.divIcon({ html, className: "", iconSize: [26, 26], iconAnchor: [13, 13] });
}

// The one NEW marker this redesign adds -- today the off-route SEGMENT is a
// red line with no single point saying "here's how bad it got." Labeled
// with the real peak distance, same number the Alerts card itself shows.
function deviationIcon(milesOff) {
  const label = milesOff != null ? `${milesOff}mi off` : "off route";
  const html = `
    <div style="display:flex;flex-direction:column;align-items:center;pointer-events:none;">
      <div style="
        font-size:10.5px; font-weight:800; color:#fff; background:#d9564a;
        padding:2px 7px; border-radius:999px; white-space:nowrap; margin-bottom:3px;
        box-shadow:0 2px 6px rgba(0,0,0,.35);
      ">${label}</div>
      <svg width="28" height="26" viewBox="0 0 24 22" style="filter:drop-shadow(0 3px 6px rgba(0,0,0,.35));">
        <path d="M12 0 23.5 20.5H0.5z" fill="#d9564a" stroke="#0b1020" stroke-width="0.8"/>
        <rect x="11" y="7" width="2" height="6" fill="#fff"/>
        <rect x="11" y="14.5" width="2" height="2" fill="#fff"/>
      </svg>
    </div>`;
  return L.divIcon({ html, className: "", iconSize: [90, 50], iconAnchor: [45, 26] });
}

function startEndIcon(kind) {
  if (kind === "end") {
    const html = `
      <svg width="22" height="30" viewBox="0 0 22 30" style="filter:drop-shadow(0 3px 6px rgba(0,0,0,.35));">
        <rect x="2" y="2" width="2.2" height="26" fill="#101828"/>
        <path d="M4.2 3 L19 7.5 L4.2 12 Z" fill="#4cae6d"/>
      </svg>`;
    return L.divIcon({ html, className: "", iconSize: [22, 30], iconAnchor: [4, 28] });
  }
  const html = `
    <svg width="24" height="32" viewBox="0 0 26 34" style="filter:drop-shadow(0 3px 6px rgba(0,0,0,.35));">
      <path d="M13 0C5.8 0 0 5.8 0 13c0 9.7 13 21 13 21s13-11.3 13-21C26 5.8 20.2 0 13 0z" fill="#101828"/>
      <circle cx="13" cy="13" r="5.5" fill="#fff"/>
    </svg>`;
  return L.divIcon({ html, className: "", iconSize: [24, 32], iconAnchor: [12, 32] });
}

function MapFitToPoints({ points, fitKey }) {
  const map = useMap();
  const lastKeyRef = React.useRef(null);
  useEffect(() => {
    if (!points || points.length < 2) return;
    if (fitKey !== undefined && fitKey === lastKeyRef.current) return;
    lastKeyRef.current = fitKey;
    map.fitBounds(L.latLngBounds(points), { padding: [60, 60], maxZoom: 7 });
  }, [points, fitKey, map]);
  return null;
}

export default function RouteMonitorModal({ loadId, unitNumber, alertContext, onClose, themeClassName, themeVars }) {
  const tileUrl = useTileLayerUrl();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scrubIndex, setScrubIndex] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!loadId) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${apiBaseUrl}/scripted/fleet/route-monitor`, { ...authHeaders(), params: { load_id: loadId } })
      .then((res) => {
        setData(res.data);
        setScrubIndex(Math.max(0, (res.data.breadcrumbs?.length || 1) - 1));
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [loadId]);

  useEffect(() => {
    const total = data?.breadcrumbs?.length || 0;
    if (!playing || total === 0) return;
    if (scrubIndex >= total - 1) {
      setPlaying(false);
      return;
    }
    const t = setTimeout(() => setScrubIndex((i) => Math.min(i + 1, total - 1)), 400);
    return () => clearTimeout(t);
  }, [playing, scrubIndex, data]);

  const breadcrumbs = data?.breadcrumbs || [];
  // The deadhead approach trail (truck_deadhead_breadcrumbs.next_load_id) -- the
  // path from when the truck went empty and started heading to THIS pickup, up to
  // pickup. Shown in full (not scrubbed); the loaded trail below keeps the scrubber.
  // Empty until the truck is actually en route to this load's pickup (for a queued
  // load that's only after its current load is delivered -- the capture gates it).
  const deadhead = data?.deadhead_breadcrumbs || [];
  const lastDeadhead = deadhead.length ? deadhead[deadhead.length - 1] : null;
  const visible = breadcrumbs.slice(0, scrubIndex + 1);
  const current = visible[visible.length - 1] || null;
  const plannedMid = data?.planned_route?.[Math.floor((data.planned_route.length || 1) / 2)];
  const mapCenter = current
    ? [current.lat, current.lng]
    : plannedMid || (lastDeadhead ? [lastDeadhead.lat, lastDeadhead.lng] : [39.5, -98.35]);

  // The one new derived value this redesign needs -- the single worst
  // off-route breadcrumb, same "peak" concept off_route_events already
  // tracks server-side (fleet_alert_events.metadata.max_deviation_miles),
  // just recomputed here from the raw breadcrumb list this endpoint
  // already returns, so no new API field is required.
  const peakDeviation = breadcrumbs.reduce((worst, b) => {
    if (!b.is_off_route || b.distance_from_route_miles == null) return worst;
    if (!worst || b.distance_from_route_miles > worst.distance_from_route_miles) return b;
    return worst;
  }, null);

  const startPoint = data?.has_planned_route ? data.planned_route[0] : breadcrumbs[0] ? [breadcrumbs[0].lat, breadcrumbs[0].lng] : null;
  const endPoint = data?.has_planned_route
    ? data.planned_route[data.planned_route.length - 1]
    : breadcrumbs.length
    ? [breadcrumbs[breadcrumbs.length - 1].lat, breadcrumbs[breadcrumbs.length - 1].lng]
    : null;

  // ---- Upcoming vs genuinely-missed planned fuel stops ----
  // A planned stop the truck simply hasn't reached yet must NOT read as
  // "NOT DETECTED" -- that made healthy in-transit trips look like they were
  // failing every stop still ahead of the truck. Project the truck's latest
  // position and each planned stop onto the planned route (nearest-vertex
  // index) and compare: a stop ahead of the truck is "upcoming"; a stop behind
  // it that was never filled is a real "missed". Only live loads get the
  // upcoming treatment -- a delivered/cancelled load is over, so any unfilled
  // planned stop is genuinely missed. Uses the LAST breadcrumb (true current
  // position), not `current` (which follows the play-the-trip scrubber).
  const plannedRoute = data?.planned_route || [];
  const latestPos = breadcrumbs.length ? breadcrumbs[breadcrumbs.length - 1] : null;
  const loadIsOver = ["delivered", "cancelled"].includes(data?.load_status);
  const nearestRouteIdx = (lat, lng) => {
    if (lat == null || lng == null || plannedRoute.length < 2) return null;
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < plannedRoute.length; i++) {
      const dLat = plannedRoute[i][0] - lat;
      const dLng = plannedRoute[i][1] - lng;
      const d = dLat * dLat + dLng * dLng;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  };
  const truckRouteIdx = latestPos ? nearestRouteIdx(latestPos.lat, latestPos.lng) : null;
  // Small slack so a stop the truck is right at (fill not yet recorded) still
  // reads "upcoming", not a premature "missed".
  const routeIdxSlack = Math.max(2, Math.floor(plannedRoute.length * 0.01));
  const fuelStopStatus = (s) => {
    if (s.matched) return "hit";
    if (loadIsOver) return "missed";
    const sIdx = nearestRouteIdx(s.planned_lat, s.planned_lng);
    // Can't localize (no route / no position / no stop coords): on a live load,
    // assume upcoming rather than cry wolf.
    if (sIdx == null || truckRouteIdx == null) return "upcoming";
    return sIdx >= truckRouteIdx - routeIdxSlack ? "upcoming" : "missed";
  };
  const upcomingFuelStops = (data?.fuel_stops || []).filter((s) => fuelStopStatus(s) === "upcoming").length;

  return createPortal(
    <div
      className={themeClassName}
      style={{
        ...themeVars,
        position: "fixed", inset: 0, zIndex: 2000, background: "rgba(2,6,23,0.72)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "92vw", height: "88vh", background: "var(--card)",
          border: "1px solid var(--line)", borderRadius: 14, padding: 14,
          display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>
            Route monitor -- Truck {unitNumber || data?.truck_unit_number || "?"}
            {data && (
              <span style={{ fontWeight: 400, color: "var(--muted)", marginLeft: 8 }}>
                {data.origin} → {data.destination}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              fontSize: 12, fontWeight: 600, color: "var(--ink)", background: "var(--line)",
              border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer",
            }}
          >
            <Minimize2 size={13} /> Close
          </button>
        </div>

        {/* Only rendered when opened FROM an alert -- Route History's own
            row click has no alert to reference, so this stays absent there. */}
        {alertContext && (
          <div
            style={{
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              margin: "0 0 10px", padding: "10px 14px", background: "rgba(217,86,74,0.08)",
              border: "1px solid rgba(217,86,74,0.28)", borderRadius: 10, fontSize: 12.5,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#d9564a", flexShrink: 0 }} />
            <span style={{ fontWeight: 700, color: "var(--ink)" }}>Opened from alert: {alertContext.leadText}</span>
            {(alertContext.figs || []).filter(Boolean).map((f, i) => (
              <span key={i} style={{ color: "var(--ink-2)" }}>{f}</span>
            ))}
            {alertContext.cause && (
              <span
                style={{
                  marginLeft: "auto", fontSize: 11.5, fontWeight: 700, color: "#d9564a",
                  background: "var(--card)", border: "1px solid rgba(217,86,74,0.35)", borderRadius: 999, padding: "3px 10px",
                }}
              >
                {alertContext.cause}
              </span>
            )}
          </div>
        )}

        {loading && <LoadingState label="Loading route history…" />}
        {error && <div style={{ color: "#e0745d", padding: 20, fontSize: 13 }}>Couldn't load the route monitor: {error}</div>}

        {!loading && !error && data && breadcrumbs.length === 0 && deadhead.length === 0 && (
          <div style={{ color: "var(--muted)", padding: 20, fontSize: 13 }}>
            No positions recorded yet. Deadhead tracking begins once the truck is empty and en route to this
            pickup (for a queued load, that's after its current load is delivered); the loaded trail begins at pickup.
          </div>
        )}

        {!loading && !error && data && (breadcrumbs.length > 0 || deadhead.length > 0) && (
          <>
            {(data.planned_toll_cost != null || data.actual_toll_count > 0 || data.fuel_cost_impact !== undefined) && (
              <div
                style={{
                  display: "flex", flexWrap: "wrap", gap: 24, marginBottom: 10, padding: "10px 14px",
                  background: "var(--card-2, var(--card))", border: "1px solid var(--line)", borderRadius: 10, fontSize: 12.5,
                }}
              >
                {(data.planned_toll_cost != null || data.actual_toll_count > 0) && (
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".03em", marginBottom: 3 }}>
                      Tolls
                    </div>
                    <div style={{ color: "var(--ink)" }}>
                      Planned <b>${(data.planned_toll_cost ?? 0).toFixed(2)}</b>
                      <span style={{ color: "var(--muted)", margin: "0 6px" }}>vs</span>
                      Actual{" "}
                      <b style={{ color: (data.actual_toll_total ?? 0) > (data.planned_toll_cost ?? 0) ? "#e0745d" : "#4cae6d" }}>
                        ${(data.actual_toll_total ?? 0).toFixed(2)}
                      </b>
                    </div>
                  </div>
                )}
                {data.fuel_cost_impact !== undefined && (
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".03em", marginBottom: 3 }}>
                      Fuel Cost vs Plan
                      {/* FINAL once the load is delivered/cancelled and the
                          finalize business rule has written a permanent
                          snapshot; LIVE means it's a fresh recompute off
                          current data and can still move as the load
                          progresses. Never let a dispatcher mistake a
                          provisional number for the settled one. */}
                      <span
                        style={{
                          marginLeft: 6, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".03em",
                          color: data.fuel_is_final ? "#4cae6d" : "#f2a33d",
                        }}
                      >
                        {data.fuel_is_final ? "FINAL" : "LIVE / IN TRANSIT"}
                      </span>
                    </div>
                    <div style={{ color: fuelImpactColor(data.fuel_cost_impact), fontWeight: 700 }}>
                      {fmtFuelImpact(data.fuel_cost_impact)}
                    </div>
                    {/* Only gallons comparable to a plan are priced above --
                        a driver buying more/fewer gallons than planned is a
                        capacity difference, not an economic one, so it's
                        shown here as coverage context, never folded into
                        the dollar figure itself. */}
                    {data.fuel_comparable_gallons != null && data.fuel_actual_gallons != null && (
                      <div style={{ color: "var(--muted)", marginTop: 2, fontSize: 11.5 }}>
                        {Math.round(data.fuel_comparable_gallons).toLocaleString("en-US")} of{" "}
                        {Math.round(data.fuel_actual_gallons).toLocaleString("en-US")} gal comparable to a plan
                        {data.fuel_coverage_pct != null && ` (${data.fuel_coverage_pct}%)`}
                      </div>
                    )}
                    {/* Trip-wide price comparison that FOLDS IN fuel bought on the
                        deadhead to pickup (unattributed, so the load-scoped figure
                        above misses it) -- a tank is a tank, those gallons fuel this
                        trip. Price quality only; the dollar figure above remains the
                        cost-attributed one. Shown only when there was a deadhead fill. */}
                    {data.trip_fuel_price && (
                      <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px dashed var(--line)", fontSize: 11.5 }}>
                        <div style={{ fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".03em", marginBottom: 2 }}>
                          Trip-wide incl. deadhead fill
                        </div>
                        <div style={{ color: fuelImpactColor(data.trip_fuel_price.price_impact), fontWeight: 700 }}>
                          {fmtFuelImpact(data.trip_fuel_price.price_impact)}
                        </div>
                        {data.trip_fuel_price.comparable_gallons != null && data.trip_fuel_price.actual_total_gallons != null && (
                          <div style={{ color: "var(--muted)", marginTop: 2 }}>
                            {Math.round(data.trip_fuel_price.comparable_gallons).toLocaleString("en-US")} of{" "}
                            {Math.round(data.trip_fuel_price.actual_total_gallons).toLocaleString("en-US")} gal comparable
                            {data.trip_fuel_price.deadhead_gallons != null &&
                              ` (incl. ${Math.round(data.trip_fuel_price.deadhead_gallons).toLocaleString("en-US")} gal on deadhead)`}
                          </div>
                        )}
                        {data.trip_fuel_price.actual_avg_price != null && data.trip_fuel_price.planned_avg_price != null && (
                          <div style={{ color: "var(--muted)", marginTop: 2 }}>
                            ${data.trip_fuel_price.actual_avg_price.toFixed(3)}/gal actual vs ${data.trip_fuel_price.planned_avg_price.toFixed(3)}/gal planned
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Per-stop planned-vs-actual fuel breakdown. The summary bar above
                shows ONE price-impact dollar figure (comparable gallons priced
                plan-vs-actual) and silently excludes any gallons bought beyond
                plan -- which left dispatchers unable to see *why* a load's real
                fuel spend differed from the figure (higher price that day? more
                gallons?). This panel exposes both sides per stop plus the
                beyond-plan gallons, so the headline number reconciles on sight.
                All fields already come back in data.fuel_stops[]; the actuals
                previously only appeared in a map-pin tooltip. */}
            {(data.fuel_stops || []).length > 0 && (
              <div style={{ marginBottom: 12, border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden" }}>
                <div style={{ padding: "8px 14px", background: "var(--card-2, var(--card))", borderBottom: "1px solid var(--line)", fontSize: 10.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".03em" }}>
                  Fuel stops &mdash; planned vs actual
                </div>
                {(data.fuel_stops || []).map((s, i) => {
                  const pg = s.planned_gallons, pp = s.planned_price_per_gallon;
                  const ag = s.gallons_added, ap = s.price_per_gallon, ac = s.total_cost;
                  const pc = pg != null && pp != null ? pg * pp : null;
                  const ppgDelta = ap != null && pp != null ? ap - pp : null;
                  const galDelta = ag != null && pg != null ? ag - pg : null;
                  return (
                    <div key={`fuelrow-${i}`} style={{ padding: "8px 14px", borderBottom: "1px solid var(--line)", fontSize: 12.5 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                        <span style={{ color: "var(--ink)", fontWeight: 600 }}>
                          {s.sequence ?? i + 1}. {s.planned_name || "Planned station"}
                        </span>
                        {(() => {
                          const st = fuelStopStatus(s);
                          const label = st === "hit" ? "FILLED" : st === "upcoming" ? "UPCOMING" : "NOT DETECTED";
                          const color = st === "hit" ? "#4cae6d" : st === "upcoming" ? "#8a94a6" : "#e0745d";
                          return (
                            <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".03em", color }}>
                              {label}
                            </span>
                          );
                        })()}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "64px 1fr", rowGap: 2, columnGap: 8, color: "var(--muted)", fontSize: 12 }}>
                        <span>Planned</span>
                        <span style={{ color: "var(--ink)" }}>
                          {pg != null ? `${Math.round(pg)} gal` : "—"}
                          {pp != null ? ` @ $${pp.toFixed(3)}/gal` : ""}
                          {pc != null ? ` = $${pc.toFixed(2)}` : ""}
                        </span>
                        {s.matched && (
                          <>
                            <span>Actual</span>
                            <span style={{ color: "var(--ink)" }}>
                              {ag != null ? `${Math.round(ag)} gal` : "—"}
                              {ap != null ? ` @ $${ap.toFixed(3)}/gal` : ""}
                              {ac != null ? ` = $${ac.toFixed(2)}` : ""}
                              {(ppgDelta != null || galDelta != null) && (
                                <span style={{ marginLeft: 8, fontSize: 11, color: "var(--muted)" }}>
                                  {ppgDelta != null && (
                                    <b style={{ color: ppgDelta > 0.0005 ? "#e0745d" : ppgDelta < -0.0005 ? "#4cae6d" : "var(--muted)" }}>
                                      {ppgDelta >= 0 ? "+" : "−"}${Math.abs(ppgDelta).toFixed(3)}/gal
                                    </b>
                                  )}
                                  {galDelta != null && (
                                    <>
                                      {" · "}
                                      <b style={{ color: galDelta > 0.5 ? "#e0745d" : galDelta < -0.5 ? "#4cae6d" : "var(--muted)" }}>
                                        {galDelta >= 0 ? "+" : "−"}{Math.abs(Math.round(galDelta))} gal
                                      </b>{" vs plan"}
                                    </>
                                  )}
                                </span>
                              )}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
                {(data.unplanned_fuel_stop_events || []).map((s, i) => (
                  <div key={`unplannedrow-${i}`} style={{ padding: "8px 14px", borderBottom: "1px solid var(--line)", fontSize: 12.5 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                      <span style={{ color: "var(--ink)", fontWeight: 600 }}>{s.station_name || "Unrecognized station"}</span>
                      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".03em", color: "#f2a33d" }}>UNPLANNED</span>
                    </div>
                    <div style={{ color: "var(--ink)", fontSize: 12 }}>
                      {s.gallons_added != null ? `${Math.round(s.gallons_added)} gal` : "—"}
                      {s.price_per_gallon != null ? ` @ $${s.price_per_gallon.toFixed(3)}/gal` : ""}
                      {s.total_cost != null ? ` = $${s.total_cost.toFixed(2)}` : ""}
                      {s.price_confidence === "estimated" ? " (price estimated, fleet avg)" : ""}
                    </div>
                  </div>
                ))}
                {(data.fuel_cost_impact != null || (data.fuel_actual_gallons != null && data.fuel_comparable_gallons != null)) && (
                  <div style={{ padding: "8px 14px", background: "var(--card-2, var(--card))", fontSize: 11.5, color: "var(--muted)", display: "flex", flexWrap: "wrap", gap: 16 }}>
                    {data.fuel_cost_impact != null && (
                      <span>
                        Price impact on comparable gallons:{" "}
                        <b style={{ color: fuelImpactColor(data.fuel_cost_impact) }}>{fmtFuelImpact(data.fuel_cost_impact)}</b>
                      </span>
                    )}
                    {data.fuel_actual_gallons != null && data.fuel_comparable_gallons != null &&
                      Math.round(data.fuel_actual_gallons - data.fuel_comparable_gallons) !== 0 && (
                        <span>
                          <b style={{ color: data.fuel_actual_gallons - data.fuel_comparable_gallons > 0 ? "#e0745d" : "#4cae6d" }}>
                            {data.fuel_actual_gallons - data.fuel_comparable_gallons > 0 ? "+" : "−"}
                            {Math.abs(Math.round(data.fuel_actual_gallons - data.fuel_comparable_gallons))} gal
                          </b>{" "}
                          {data.fuel_actual_gallons - data.fuel_comparable_gallons > 0 ? "bought beyond plan" : "under plan"} (not in the $ figure)
                        </span>
                      )}
                  </div>
                )}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16, marginBottom: 8, fontSize: 12, color: "var(--muted)" }}>
              <span>{breadcrumbs.length} loaded position{breadcrumbs.length === 1 ? "" : "s"} recorded</span>
              {deadhead.length > 0 && (
                <span style={{ color: "#f2a33d" }}>
                  {deadhead.length} deadhead approach position{deadhead.length === 1 ? "" : "s"}
                  {breadcrumbs.length === 0 ? " (not picked up yet)" : ""}
                </span>
              )}
              {(data.deadhead_fuel_stops || []).length > 0 && (
                <span style={{ color: "#3b82f6" }}>
                  {data.deadhead_fuel_stops.length} fuel stop{data.deadhead_fuel_stops.length === 1 ? "" : "s"} on approach
                </span>
              )}
              <span style={{ color: data.off_route_breadcrumb_count > 0 ? "#e0745d" : "var(--muted)" }}>
                {data.off_route_breadcrumb_count} off-route (&gt;{data.route_deviation_threshold_miles}mi from plan)
              </span>
              {data.actual_toll_count > 0 && (
                <span>${data.actual_toll_total.toLocaleString("en-US")} in tolls detected</span>
              )}
              {data.planned_fuel_stops > 0 && (() => {
                // Only count stops the truck has actually passed-without-filling as
                // "missed" for the alarm color -- stops still ahead of the truck are
                // upcoming, not failures.
                const realMissed = Math.max(0, data.planned_fuel_stops - data.matched_fuel_stops - upcomingFuelStops);
                return (
                  <span style={{ color: realMissed > 0 ? "#e0745d" : "var(--muted)" }}>
                    {data.matched_fuel_stops}/{data.planned_fuel_stops} planned fuel stops hit
                    {upcomingFuelStops > 0 ? ` · ${upcomingFuelStops} upcoming` : ""}
                  </span>
                );
              })()}
              {data.unplanned_fuel_stops > 0 && (
                <span style={{ color: "#e0745d" }}>
                  {data.unplanned_fuel_stops} unplanned fuel stop{data.unplanned_fuel_stops === 1 ? "" : "s"}
                </span>
              )}
              {!data.has_planned_route && (
                <span style={{ color: "#f2a33d" }}>No saved route geometry to compare against -- showing actual path only</span>
              )}
              {(data.has_planned_route || deadhead.length > 0 || (data.toll_passes || []).length > 0 || (data.fuel_stops || []).length > 0 || (data.unplanned_fuel_stop_events || []).length > 0) && (
                <span style={{ display: "flex", alignItems: "center", gap: 14, marginLeft: "auto", flexWrap: "wrap" }}>
                  {deadhead.length > 0 && (
                    <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ display: "inline-block", width: 16, height: 0, borderTop: "3px dashed #f2a33d" }} />
                      Deadhead to pickup
                    </span>
                  )}
                  {data.has_planned_route && (
                    <>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ display: "inline-block", width: 16, height: 0, borderTop: "3px dashed #8b5cf6" }} />
                        Planned route
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ display: "inline-block", width: 16, height: 3, borderRadius: 2, background: "#4cae6d" }} />
                        Actual path
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ display: "inline-block", width: 16, height: 3, borderRadius: 2, background: "#e0745d" }} />
                        Off route
                      </span>
                    </>
                  )}
                  {(data.toll_passes || []).length > 0 && (
                    <>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: "#1d6fd8" }} />
                        Toll (chosen route)
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: "#6ea3d8" }} />
                        Toll (alternate route)
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: "#d9564a" }} />
                        Toll (unplanned detour)
                      </span>
                    </>
                  )}
                  {((data.fuel_stops || []).length > 0 || (data.unplanned_fuel_stop_events || []).length > 0) && (
                    <>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: "#c9962a", border: "2px solid #c9962a" }} />
                        Fuel stop hit
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: "transparent", border: "2px solid #c9962a" }} />
                        Fuel stop missed
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: "#3b82f6" }} />
                        Unplanned stop (recognized station)
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: "#d9564a" }} />
                        Unplanned stop (unrecognized, price estimated)
                      </span>
                    </>
                  )}
                  {peakDeviation && (
                    <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ display: "inline-block", width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderBottom: "9px solid #d9564a" }} />
                      Peak deviation
                    </span>
                  )}
                </span>
              )}
            </div>

            <div style={{ flex: 1, minHeight: 0, position: "relative", borderRadius: 10, overflow: "hidden", border: "1px solid var(--line)" }}>
              <MapContainer center={mapCenter} zoom={6} style={{ height: "100%", width: "100%" }}>
                {tileUrl && <TileLayer attribution={TILE_LAYER_ATTRIBUTION} url={tileUrl} />}
                <MapFitToPoints
                  points={[
                    ...(data.planned_route || []),
                    ...(data.planned_deadhead_route || []),
                    ...deadhead.map((b) => [b.lat, b.lng]),
                    ...(data.deadhead_fuel_stops || []).map((s) => [s.lat, s.lng]).filter(([la, ln]) => la != null && ln != null),
                    ...breadcrumbs.map((b) => [b.lat, b.lng]),
                    ...(data.toll_passes || []).map((t) => [t.toll_lat, t.toll_lng]),
                    ...(data.fuel_stops || []).map((s) => [s.planned_lat, s.planned_lng]).filter(([la, ln]) => la != null && ln != null),
                    ...(data.unplanned_fuel_stop_events || []).map((s) => [s.actual_lat, s.actual_lng]).filter(([la, ln]) => la != null && ln != null),
                  ]}
                  fitKey={loadId}
                />
                {/* Deadhead approach -- drawn in full (not scrubbed) as a dashed
                    amber line that feeds into the pickup, behind the loaded trail.
                    Planned deadhead geometry (if the chosen route saved one) sits
                    under it as a fainter reference. */}
                {data.has_deadhead_route && (
                  <Polyline
                    positions={data.planned_deadhead_route}
                    pathOptions={{ color: "#f2a33d", weight: 2, opacity: 0.4, dashArray: "2 8" }}
                  />
                )}
                {deadhead.length > 1 && (
                  <Polyline
                    positions={deadhead.map((b) => [b.lat, b.lng])}
                    pathOptions={{ color: "#f2a33d", weight: 3, opacity: 0.9, dashArray: "6 6" }}
                  />
                )}
                {/* Where the truck is now while still deadheading (no loaded trail yet). */}
                {breadcrumbs.length === 0 && lastDeadhead && (
                  <Marker position={[lastDeadhead.lat, lastDeadhead.lng]} icon={startEndIcon("end")}>
                    <Tooltip>En route to pickup (last position)</Tooltip>
                  </Marker>
                )}
                {visible.slice(1).map((b, i) => (
                  <Polyline
                    key={i}
                    positions={[[visible[i].lat, visible[i].lng], [b.lat, b.lng]]}
                    pathOptions={{ color: b.is_off_route ? "#e0745d" : "#4cae6d", weight: 4 }}
                  />
                ))}
                {data.has_planned_route && (
                  <Polyline
                    positions={data.planned_route}
                    pathOptions={{ color: "#8b5cf6", weight: 3, opacity: 0.85, dashArray: "8 7" }}
                  />
                )}

                {startPoint && <Marker position={startPoint} icon={startEndIcon("start")} />}
                {endPoint && <Marker position={endPoint} icon={startEndIcon("end")} />}

                {(data.toll_passes || []).map((t, i) => (
                  <Marker key={i} position={[t.toll_lat, t.toll_lng]} icon={tollIcon(t.source)}>
                    <Tooltip>
                      {t.toll_name || "Toll"} -- ${(t.price_usd ?? 0).toFixed(2)}
                      {t.source !== "planned" && " (alternate route)"}
                    </Tooltip>
                  </Marker>
                ))}
                {(data.fuel_stops || [])
                  .filter((s) => s.planned_lat != null && s.planned_lng != null)
                  .map((s, i) => (
                    <Marker key={`fuel-planned-${i}`} position={[s.planned_lat, s.planned_lng]} icon={fuelIcon(fuelStopStatus(s))}>
                      <Tooltip>
                        Fuel stop {s.sequence ?? i + 1}: {s.planned_name || "planned station"}
                        {s.matched
                          ? ` -- hit${s.gallons_added != null ? ` (${s.gallons_added} gal)` : ""}${s.price_per_gallon != null ? ` @ $${s.price_per_gallon.toFixed(3)}/gal` : ""}${s.total_cost != null ? ` -- $${s.total_cost.toFixed(2)}` : ""}`
                          : fuelStopStatus(s) === "upcoming"
                          ? `${s.planned_price_per_gallon != null ? ` -- upcoming, not yet reached (planned $${s.planned_price_per_gallon.toFixed(3)}/gal)` : " -- upcoming, not yet reached"}`
                          : `${s.planned_price_per_gallon != null ? ` -- not detected (planned $${s.planned_price_per_gallon.toFixed(3)}/gal)` : " -- not detected"}`}
                      </Tooltip>
                    </Marker>
                  ))}
                {(data.unplanned_fuel_stop_events || [])
                  .filter((s) => s.actual_lat != null && s.actual_lng != null)
                  .map((s, i) => (
                    <Marker
                      key={`fuel-unplanned-${i}`}
                      position={[s.actual_lat, s.actual_lng]}
                      icon={fuelIcon(s.station_name ? "unplanned_recognized" : "unplanned_unrecognized")}
                    >
                      <Tooltip>
                        {s.station_name || "Unrecognized station"}
                        {s.gallons_added != null ? ` -- ${s.gallons_added} gal` : ""}
                        {s.price_per_gallon != null ? ` @ $${s.price_per_gallon.toFixed(3)}/gal` : ""}
                        {s.total_cost != null ? ` -- $${s.total_cost.toFixed(2)}` : ""}
                        {s.price_confidence === "estimated" ? " (price estimated, fleet average)" : ""}
                        , not part of the plan
                      </Tooltip>
                    </Marker>
                  ))}
                {/* Fuel stops DURING the deadhead approach (unattributed fills) --
                    "did they stop for gas on the way to pickup?" */}
                {(data.deadhead_fuel_stops || [])
                  .filter((s) => s.lat != null && s.lng != null)
                  .map((s, i) => (
                    <Marker key={`dh-fuel-${i}`} position={[s.lat, s.lng]} icon={fuelIcon("unplanned_recognized")}>
                      <Tooltip>
                        Fuel stop on the way to pickup{s.name ? ` -- ${s.name}` : ""}
                        {s.gallons_added != null ? ` -- ${s.gallons_added} gal` : ""}
                        {s.price_per_gallon != null ? ` @ $${s.price_per_gallon.toFixed(3)}/gal` : ""}
                        {s.total_cost != null ? ` -- $${s.total_cost.toFixed(2)}` : ""}
                      </Tooltip>
                    </Marker>
                  ))}
                {peakDeviation && (
                  <Marker position={[peakDeviation.lat, peakDeviation.lng]} icon={deviationIcon(peakDeviation.distance_from_route_miles)}>
                    <Tooltip>Peak deviation -- {peakDeviation.distance_from_route_miles}mi off planned route</Tooltip>
                  </Marker>
                )}
                {current && (
                  <Marker
                    position={[current.lat, current.lng]}
                    icon={truckIcon(unitNumber || "", current.is_off_route ? "#e0745d" : "#4cae6d", true)}
                  />
                )}
              </MapContainer>
            </div>

            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
              <button
                onClick={() => setPlaying((p) => !p)}
                title={playing ? "Pause" : "Fast forward through the recorded path"}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30,
                  background: "var(--panel, var(--bg))", border: "1px solid var(--line)", borderRadius: 8, cursor: "pointer",
                  color: "var(--ink)", flexShrink: 0,
                }}
              >
                {playing ? <Square size={13} /> : <Play size={13} />}
              </button>
              <input
                type="range"
                min={0}
                max={Math.max(0, breadcrumbs.length - 1)}
                value={scrubIndex}
                onChange={(e) => {
                  setPlaying(false);
                  setScrubIndex(Number(e.target.value));
                }}
                style={{ flex: 1, accentColor: "#5b9bd5", height: 6, cursor: "pointer" }}
              />
              <span style={{ fontSize: 11, color: current?.is_off_route ? "#e0745d" : "var(--muted)", minWidth: 170, textAlign: "right" }}>
                {current
                  ? `${new Date(current.recorded_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}${current.is_off_route ? ` · ${current.distance_from_route_miles}mi off route` : ""}`
                  : "—"}
              </span>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
